import { PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';
import { noop } from 'lodash';

interface DialogFormContextValue {
  open: boolean;
  setOpen: () => void;
  setClosed: () => void;
  onSuccess?: (() => void) | undefined;
  setOnSuccess: (fn: () => void) => void;
}

type CreateDialogFormContextFunc = () => {
  provider: ({ children }: PropsWithChildren) => JSX.Element;
  useContext: () => DialogFormContextValue;
};

export const createDialogFormContext: CreateDialogFormContextFunc = () => {
  const defaultContextValue = {
    open: false,
    setOpen: noop,
    setClosed: noop,
    setOnSuccess: noop,
  };

  const ctx = createContext<DialogFormContextValue>(defaultContextValue);

  const ContextProvider = ({ children }: PropsWithChildren) => {
    const [open, setOpen] = useState(false);
    const [onSuccess, setOnSuccess] = useState<() => void>();

    const value = useMemo(() => {
      const result: DialogFormContextValue = {
        open,
        setOpen: () => setOpen(true),
        setClosed: () => setOpen(false),
        onSuccess,
        setOnSuccess: (fn) => setOnSuccess(() => fn),
      };
      return result;
    }, [open, onSuccess]);

    return <ctx.Provider value={value}>{children}</ctx.Provider>;
  };

  return {
    provider: ContextProvider,
    useContext: () => useContext(ctx),
  };
};
